import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { oidcCompleteSignIn } from "./auth.js";
import { Icon, Logo } from "./components/Icon.jsx";
import { LanguageSwitcher } from "./components/LanguageSwitcher.jsx";
import { TopBar } from "./components/TopBar.jsx";
import { ErrorBanner, FullPageLoader } from "./components/ui.jsx";
import { useErrorMessage, useLocale, useLocalePath } from "./i18n/hooks.js";
import { isSupported } from "./i18n/locales.js";
import { useSession } from "./session.jsx";
import { WorkspaceProvider, useWorkspace } from "./workspace.jsx";

/** Centered card layout for login / onboarding / billing return. Mobile-friendly. */
export function AuthShell({ children, wide = false, showLang = true }) {
  const lp = useLocalePath();
  return (
    <div className="auth-shell glow-bg">
      <header className="auth-head">
        <Link to={lp("/")} className="brand">
          <Logo size={28} />
          <span className="brand-name">
            <FormattedMessage id="brand.name" />
          </span>
        </Link>
        {showLang ? <LanguageSwitcher saveToProfile={false} /> : null}
      </header>
      <main className={`auth-main ${wide ? "is-wide" : ""}`}>{children}</main>
      <footer className="auth-foot muted small">
        <FormattedMessage id="auth.footer" values={{ year: new Date().getFullYear() }} />
      </footer>
    </div>
  );
}

function SessionError() {
  const { error, boot } = useSession();
  const errMsg = useErrorMessage();
  return (
    <AuthShell>
      <div className="card auth-card">
        <h1 className="h2">
          <FormattedMessage id="session.errorTitle" />
        </h1>
        <ErrorBanner message={errMsg(error)} onRetry={boot} />
      </div>
    </AuthShell>
  );
}

/** Applies the profile locale once after a first-visit auto-detected redirect. */
function useProfileLocale() {
  const { user } = useSession();
  const locale = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    let flag = null;
    try {
      flag = sessionStorage.getItem("pharos.autoLocale");
      sessionStorage.removeItem("pharos.autoLocale");
    } catch {
      flag = null;
    }
    if (flag && user?.locale && user.locale !== locale && isSupported(user.locale)) {
      const parts = location.pathname.split("/");
      parts[1] = user.locale;
      navigate(parts.join("/") + location.search, { replace: true });
    }
    // run once per signed-in user
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}

export function RequireAuth() {
  const { status } = useSession();
  const location = useLocation();
  const lp = useLocalePath();
  useProfileLocale();
  if (status === "loading") return <FullPageLoader />;
  if (status === "error") return <SessionError />;
  if (status === "anon") {
    const next = location.pathname + location.search;
    return <Navigate to={`${lp("/login")}?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

export function RequireMembership({ children }) {
  const { memberships } = useSession();
  const lp = useLocalePath();
  if (!memberships.length) return <Navigate to={lp("/onboarding")} replace />;
  return children;
}

function SideRail() {
  const intl = useIntl();
  const lp = useLocalePath();
  const items = [
    { to: lp("/"), icon: "dashboard", id: "nav.dashboard", end: true },
    { to: lp("/investigations"), icon: "folder", id: "nav.investigations", end: true },
    { to: lp("/investigations/new"), icon: "plus", id: "nav.newInvestigation" },
    { to: lp("/settings"), icon: "settings", id: "nav.settings" },
  ];
  return (
    <nav className="rail" aria-label={intl.formatMessage({ id: "nav.label" })}>
      {items.map((it) => (
        <NavLink
          key={it.id}
          to={it.to}
          end={it.end}
          className={({ isActive }) => `rail-link ${isActive ? "is-active" : ""}`}
          title={intl.formatMessage({ id: it.id })}
        >
          <Icon name={it.icon} size={20} />
          <span className="rail-label">
            <FormattedMessage id={it.id} />
          </span>
        </NavLink>
      ))}
    </nav>
  );
}

function ClearSearchOnNavigate() {
  const { pathname } = useLocation();
  const { setSearch } = useWorkspace();
  useEffect(() => setSearch(""), [pathname, setSearch]);
  return null;
}

export function WorkspaceLayout() {
  const { tenantId } = useSession();
  return (
    <WorkspaceProvider>
      <ClearSearchOnNavigate />
      <div className="ws glow-bg">
        <TopBar />
        <div className="ws-body">
          <SideRail />
          <main className="ws-main" key={tenantId || "none"}>
            <Outlet />
          </main>
        </div>
      </div>
    </WorkspaceProvider>
  );
}

/** OIDC redirect target (outside the locale tree because the redirect URI must be fixed). */
export function OidcCallback() {
  const { authConfig, status, refreshMe } = useSession();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!authConfig || authConfig.mode !== "oidc") return;
    let alive = true;
    oidcCompleteSignIn(authConfig)
      .then(async (returnTo) => {
        await refreshMe();
        if (alive) navigate(returnTo || "/", { replace: true });
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [authConfig, navigate, refreshMe]);
  if (failed || (status !== "loading" && authConfig && authConfig.mode !== "oidc")) {
    return <Navigate to="/" replace />;
  }
  return <FullPageLoader />;
}
